<template>
  <form @submit.prevent="handleSubmit" class="donation-form">
    <!-- Patron Selection with Quick-Add -->
    <div class="form-field">
      <IntercomSelect
        v-model="formData.patron_id"
        :label="$t('donation.patron')"
        :options="patronOptions"
        :placeholder="$t('donation.selectPatron')"
        required
        filterable
        :disabled="loading || isCreatingPatron"
        @update:model-value="handlePatronSelect"
      >
        <template #option="{ option }">
          <span :class="{ 'add-new-option': option.isCreateNew }">
            {{ option.label }}
          </span>
        </template>
      </IntercomSelect>

      <!-- Inline New Patron Form -->
      <div v-if="showNewPatronForm" class="new-patron-form">
        <div class="new-patron-header">
          <span class="new-patron-title">{{ $t('patron.addPatron') }}</span>
        </div>
        <div class="new-patron-fields">
          <IntercomInput
            v-model="newPatron.name"
            :label="$t('patron.name')"
            placeholder="Enter patron name"
            required
            :disabled="isCreatingPatron"
          />
          <IntercomPhoneInput
            v-model="newPatron.phone"
            :label="$t('patron.phone')"
            :disabled="isCreatingPatron"
            @update:valid="(v) => (newPatronPhoneValid = v)"
          />
          <IntercomSelect
            v-model="newPatron.patron_type"
            :label="$t('patron.type')"
            :options="patronTypeOptions"
            :disabled="isCreatingPatron"
          />
          <IntercomEmailInput
            v-model="newPatron.email"
            :label="$t('patron.email')"
            placeholder="Optional email"
            :disabled="isCreatingPatron"
          />
        </div>
        <div class="new-patron-actions">
          <IntercomButton
            type="button"
            variant="ghost"
            size="sm"
            @click="cancelNewPatron"
            :disabled="isCreatingPatron"
          >
            {{ $t('common.cancel') }}
          </IntercomButton>
          <IntercomButton
            type="button"
            variant="primary"
            size="sm"
            @click="createAndSelectPatron"
            :loading="isCreatingPatron"
            :disabled="!newPatron.name || (newPatron.phone && !newPatronPhoneValid)"
          >
            {{ $t('forms.createAndSelect') }}
          </IntercomButton>
        </div>
      </div>
    </div>

    <div class="form-field">
      <IntercomSelect
        v-model="formData.donation_type"
        :label="$t('donation.type')"
        :options="typeOptions"
        required
        :disabled="loading"
      />
    </div>

    <div v-if="formData.donation_type === 'money' || formData.donation_type === 'in_kind'" class="form-field">
      <IntercomSelect
        v-model="formData.fund_type"
        :label="'Fund Type'"
        :options="fundTypeOptions"
        :disabled="loading"
      />
    </div>

    <div v-if="formData.donation_type === 'money'" class="form-field">
      <IntercomSelect
        v-model="formData.payment_mode"
        :label="'Payment Mode'"
        :options="paymentModeOptions"
        :placeholder="'Select payment mode'"
        :disabled="loading"
      />
    </div>

    <div v-if="formData.donation_type !== 'volunteer'" class="form-field">
      <IntercomInput
        v-model.number="formData.amount"
        :label="amountLabel"
        type="number"
        :placeholder="amountPlaceholder"
        :required="formData.donation_type === 'money'"
        :disabled="loading"
      />
      <span v-if="formData.donation_type === 'in_kind'" class="field-hint">
        {{ $t('donation.estimatedValueHint') }}
      </span>
    </div>

    <div v-if="formData.donation_type === 'volunteer'" class="form-field">
      <IntercomInput
        v-model.number="formData.volunteer_hours"
        :label="$t('donation.volunteerHours')"
        type="number"
        placeholder="0"
        :disabled="loading"
      />
    </div>

    <div class="form-field">
      <IntercomDateInput
        v-model="formData.donation_date"
        :label="$t('donation.date')"
        required
        :disabled="loading"
      />
    </div>

    <div class="form-field">
      <IntercomInput
        v-model="formData.receipt_number"
        :label="$t('donation.receiptNumber')"
        placeholder="Optional receipt number"
        :disabled="loading"
      />
    </div>

    <div class="form-field">
      <IntercomInput
        v-model="formData.description"
        :label="$t('donation.description')"
        :placeholder="descriptionPlaceholder"
        :disabled="loading"
      />
    </div>

    <div class="form-field">
      <IntercomInput
        v-model="formData.notes"
        :label="$t('donation.notes')"
        placeholder="Optional notes"
        :disabled="loading"
      />
    </div>

    <!-- Bank Account Selection -->
    <div v-if="bankAccountOptions.length > 0" class="form-field">
      <IntercomSelect
        v-model="formData.bank_account_id"
        label="Received in Account"
        :options="bankAccountOptions"
        placeholder="Select account"
        :disabled="loading"
      />
    </div>

    <!-- Cash > Rs 2,000 Warning -->
    <div
      v-if="showCashWarning"
      class="cash-warning-banner"
    >
      Cash donations above Rs 2,000 are not eligible for 80G tax deduction under Section 80G(5D). This donation will be marked as 80G ineligible.
    </div>

    <div class="form-actions">
      <IntercomButton type="button" variant="ghost" @click="$emit('cancel')" :disabled="loading">
        {{ $t('common.cancel') }}
      </IntercomButton>
      <IntercomButton type="submit" variant="primary" :loading="loading">
        {{ $t('common.save') }}
      </IntercomButton>
    </div>
  </form>
</template>

<script setup>
import { ref, reactive, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast } from '@shared/composables/useToast.js'
import dayjs from 'dayjs'
import {
  IntercomInput,
  IntercomDateInput,
  IntercomPhoneInput,
  IntercomEmailInput,
  IntercomSelect,
  IntercomButton,
} from '@shared/components/intercom'
import { useSeva } from '../composables/useSeva.js'

const { t } = useI18n()
const toast = useToast()
const { createPatron } = useSeva()

const props = defineProps({
  donation: { type: Object, default: null },
  patrons: { type: Array, default: () => [] },
  bankAccounts: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  defaultPatronId: { type: [Number, String], default: null },
})

const emit = defineEmits(['submit', 'cancel', 'patron-created'])

const formData = reactive({
  patron_id: null,
  donation_type: 'money',
  fund_type: 'general',
  payment_mode: null,
  amount: null,
  volunteer_hours: null,
  donation_date: dayjs().format('YYYY-MM-DD'),
  receipt_number: '',
  description: '',
  notes: '',
  bank_account_id: null,
})

// Quick-add patron state
const showNewPatronForm = ref(false)
const isCreatingPatron = ref(false)
const newPatronPhoneValid = ref(false)
const newPatron = reactive({
  name: '',
  phone: '',
  email: '',
  patron_type: 'individual',
})

// Local list of inline-created patrons (avoids prop round-trip timing issues)
const inlineCreatedPatrons = ref([])

const patronOptions = computed(() => {
  // Merge props with locally created patrons, deduplicate by ID
  const seen = new Set()
  const allPatrons = [...props.patrons, ...inlineCreatedPatrons.value]
  const unique = allPatrons.filter(p => {
    if (seen.has(p.id)) return false
    seen.add(p.id)
    return true
  })
  const options = unique.map(p => ({ value: p.id, label: p.name }))
  options.push({ value: '__create_new__', label: '+ ' + t('patron.addPatron'), isCreateNew: true })
  return options
})

const typeOptions = computed(() => [
  { value: 'money', label: t('donation.types.money') },
  { value: 'in_kind', label: t('donation.types.in_kind') },
  { value: 'volunteer', label: t('donation.types.volunteer') },
])

const patronTypeOptions = computed(() => [
  { value: 'individual', label: t('patron.types.individual') },
  { value: 'corporate', label: t('patron.types.corporate') },
  { value: 'foundation', label: t('patron.types.foundation') },
])

const fundTypeOptions = computed(() => [
  { value: 'general', label: 'General' },
  { value: 'corpus', label: 'Corpus (Permanent Fund)' },
  { value: 'specific', label: 'Specific Purpose' },
])

const paymentModeOptions = computed(() => [
  { value: 'cash', label: 'Cash' },
  { value: 'upi', label: 'UPI' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'dd', label: 'Demand Draft' },
  { value: 'online', label: 'Online' },
])

const amountLabel = computed(() => {
  return formData.donation_type === 'in_kind'
    ? t('donation.estimatedValue')
    : t('donation.amount')
})

const amountPlaceholder = computed(() => {
  return formData.donation_type === 'in_kind'
    ? t('donation.estimatedValuePlaceholder')
    : '0.00'
})

const descriptionPlaceholder = computed(() => {
  switch (formData.donation_type) {
    case 'money': return 'e.g., Monthly donation, Festival contribution'
    case 'in_kind': return 'e.g., Rice bags, Cooking oil, Vegetables'
    case 'volunteer': return 'e.g., Kitchen help, Event organization'
    default: return 'Description'
  }
})

const bankAccountOptions = computed(() => {
  return props.bankAccounts
    .filter(a => a.is_active)
    .map(a => ({ value: a.id, label: `${a.name}${a.bank_name ? ' (' + a.bank_name + ')' : ''}${a.bank_account_number ? ' - ' + a.bank_account_number : ''}` }))
})

const showCashWarning = computed(() => {
  return formData.donation_type === 'money' &&
    formData.amount > 2000 &&
    formData.payment_mode === 'cash'
})

// Populate form when donation prop changes (edit mode)
watch(() => props.donation, (newDonation) => {
  if (newDonation) {
    formData.patron_id = newDonation.patron_id || null
    formData.donation_type = newDonation.donation_type || 'money'
    formData.amount = newDonation.amount || null
    formData.volunteer_hours = newDonation.volunteer_hours || null
    formData.donation_date = newDonation.donation_date
      ? dayjs(newDonation.donation_date).format('YYYY-MM-DD')
      : dayjs().format('YYYY-MM-DD')
    formData.receipt_number = newDonation.receipt_number || ''
    formData.description = newDonation.description || ''
    formData.notes = newDonation.notes || ''
    formData.fund_type = newDonation.fund_type || 'general'
    formData.payment_mode = newDonation.payment_mode || null
    formData.bank_account_id = newDonation.bank_account_id || null
  } else if (props.defaultPatronId) {
    formData.patron_id = Number(props.defaultPatronId)
  }
}, { immediate: true })

const resetNewPatronForm = () => {
  newPatron.name = ''
  newPatron.phone = ''
  newPatron.email = ''
  newPatron.patron_type = 'individual'
  newPatronPhoneValid.value = false
}

const cancelNewPatron = () => {
  showNewPatronForm.value = false
  resetNewPatronForm()
}

const handlePatronSelect = (value) => {
  if (value === '__create_new__') {
    showNewPatronForm.value = true
    // Reset selection so the special option isn't displayed as selected
    formData.patron_id = null
  } else {
    // Explicitly set the patron_id for normal selections
    formData.patron_id = value
  }
}

const createAndSelectPatron = async () => {
  if (!newPatron.name) {
    toast.error(t('errors.nameRequired'))
    return
  }

  isCreatingPatron.value = true
  try {
    // Strip +91 prefix — backend expects 10 raw digits
    let phone = newPatron.phone || null
    if (phone && phone.startsWith('+91')) phone = phone.slice(3)

    const response = await createPatron({
      ...newPatron,
      phone: phone || null,
    })
    const created = response?.patron || response

    // Add to local list immediately so patronOptions includes it
    inlineCreatedPatrons.value = [...inlineCreatedPatrons.value, created]

    // Auto-select the newly created patron
    formData.patron_id = created.id

    // Notify parent so it can refresh its own list
    emit('patron-created', created)

    // Close form and reset
    showNewPatronForm.value = false
    resetNewPatronForm()

    toast.success(t('success.patronCreated'))
  } catch (error) {
    console.error('Failed to create patron:', error)
    toast.error(error.detail || error.message || t('errors.saveFailed'))
  } finally {
    isCreatingPatron.value = false
  }
}

const handleSubmit = () => {
  const data = { ...formData }
  // Clear irrelevant fields based on type
  if (data.donation_type === 'volunteer') {
    data.amount = null
    data.fund_type = 'general'  // reset, not meaningful for volunteer
    data.payment_mode = null
  } else {
    data.volunteer_hours = null
  }
  if (data.donation_type === 'in_kind') {
    data.payment_mode = null  // in-kind has no payment mode
  }
  emit('submit', data)
}
</script>

<style scoped>
.donation-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.form-field {
  display: flex;
  flex-direction: column;
}

.field-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-charcoal);
  margin-bottom: var(--space-1);
}

.required {
  color: var(--color-red-500);
}

.field-hint {
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
  margin-top: var(--space-1);
}

.add-new-option {
  color: var(--color-blue);
  font-weight: var(--font-weight-medium);
}

.new-patron-form {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: var(--radius-lg);
  padding: var(--space-4);
  margin-top: var(--space-3);
}

.new-patron-header {
  margin-bottom: var(--space-3);
}

.new-patron-title {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-blue-700);
}

.new-patron-fields {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--space-3);
}

.new-patron-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-2);
  margin-top: var(--space-4);
  padding-top: var(--space-3);
  border-top: 1px solid #bfdbfe;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
  margin-top: var(--space-4);
  padding-top: var(--space-4);
  border-top: 1px solid var(--color-gray-200);
}

.cash-warning-banner {
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: var(--radius-lg);
  padding: var(--space-3) var(--space-4);
  font-size: var(--font-size-sm);
  color: #9a3412;
  line-height: 1.5;
}

@media (max-width: 640px) {
  .new-patron-fields {
    grid-template-columns: 1fr;
  }
}
</style>
